// A PERSON IS PUSHING TO THE PULL REQUEST — the babysit waits for the branch to go quiet.
//
// THE MEASURED FAULT (tesote/tesote.ai#2724, 2026-09-28). A babysit follow-up worked the PR
// while its author pushed three times in 25 minutes (12:53, 13:03, 13:15); two runs died
// `remote_moved` and each was re-driven within seconds onto a head that moved again. The
// head watch (#5402) stops a run within a minute of a move; this is the other half: do not
// START (or re-start) a round while a person is still pushing.
//
// THE WINDOW, CHOSEN FROM DATA — prod `inbound_events`, `pull_request.synchronize` sent by a
// `User` (never a bot), 7 days to 2026-09-28: 1,135 human pushes on 537 PRs, 598 of them
// followed by another push on the same PR. Gaps between consecutive human pushes: p50 15 min,
// p75 36, p80 44, p90 75 (tesote/tesote.ai: 78% within 30 min). A run is killed when the next
// push lands inside its median 25-minute duration, so the window buys that risk down:
//
//     window   P(next push lands in the run that starts when it lapses)
//     15 min   ~20%
//     30 min   ~11%
//     45 min   ~7%
//
// 30 minutes: it halves the kill rate of 15 and costs half an hour of latency once per burst,
// where 45 buys four more points for another 15 minutes on EVERY human push. What is left is
// the head watch's to catch within a minute.
//
// THE BOUND, ALSO FROM DATA: chains of human pushes each within 30 minutes of the last lasted
// p99 1.3 h and at most 1.82 h over the same 7 days, so a deferral (chain + window) never
// exceeded ~2.3 h. {@link HUMAN_PUSHING_SURFACE_MS} (3 h) is past every observed one: a PR
// deferred that long is surfaced on `next_steps`, never silently re-deferred forever.
//
// WHO IS A PERSON: a push whose GitHub account is not `type: 'Bot'` (our own
// `developerz-ai[bot]` pushes, dependabot, renovate). An account GitHub cannot map is a
// person — the conservative side, since a wrong "person" costs one window and a wrong "bot"
// costs a run.

/** How long the branch must be quiet after a person's push before a babysit round starts. */
export const HUMAN_PUSH_QUIET_MS = 30 * 60_000;

/** How long a PR may keep a babysit deferred before `next_steps` surfaces it. */
export const HUMAN_PUSHING_SURFACE_MS = 3 * 60 * 60_000;

/** The run event a deferral rides: when it lifts, since when the person has been pushing, and which head. */
export const BABYSIT_DEFERRED_EVENT = 'babysit_deferred' as const;

/** One push on the PR: when it landed, and whether a person made it. */
export type PrPush = { readonly at: number; readonly human: boolean };

/** A person is pushing: the round waits until `quietUntil`; they have been at it since `pushingSince`. */
export type HumanPushing = { readonly quietUntil: number; readonly pushingSince: number };

/**
 * Is a person still pushing? PURE. `null` when the latest push is a bot's, or older than the
 * window. `pushingSince` walks back over the person's pushes that each landed within the
 * window of the next — the burst this deferral belongs to.
 */
export function humanPushing(
  pushes: readonly PrPush[],
  now: number,
  windowMs: number = HUMAN_PUSH_QUIET_MS,
): HumanPushing | null {
  const ordered = [...pushes].sort((a, b) => a.at - b.at);
  const last = ordered.at(-1);
  if (last === undefined || !last.human || now - last.at >= windowMs) return null;
  let since = last.at;
  for (let i = ordered.length - 2; i >= 0; i--) {
    const push = ordered[i];
    if (push === undefined || !push.human || since - push.at >= windowMs) break;
    since = push.at;
  }
  return { quietUntil: last.at + windowMs, pushingSince: since };
}

const QUIET_MARK = 'quiet until ';

/**
 * The refusal message a deferral ends a run with — carrying the instant it lifts in a form
 * {@link parseHumanQuietUntil} reads back, so the failure write can park the task until
 * exactly then (`statedKeyResetOf`, `apps/worker`). One pair, so the two cannot drift.
 */
export function humanPushingMessage(pushing: HumanPushing, who: string | null): string {
  return (
    `${who ?? 'a person'} is pushing to this pull request (since ${new Date(pushing.pushingSince).toISOString()}), ` +
    `so the babysit round waits for the branch to go quiet — ${QUIET_MARK}${new Date(pushing.quietUntil).toISOString()}. ` +
    'Nothing failed and no attempt was charged; it starts on its own once nobody has pushed for the quiet window.'
  );
}

/** The instant a {@link humanPushingMessage} names, or `null`. PURE. */
export function parseHumanQuietUntil(message: string): Date | null {
  const at = message.indexOf(QUIET_MARK);
  if (at < 0) return null;
  const iso = message
    .slice(at + QUIET_MARK.length)
    .match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/);
  if (iso === null) return null;
  const when = new Date(iso[0]);
  return Number.isNaN(when.getTime()) ? null : when;
}
